import { useState, useRef, useCallback } from 'react';
import { FunctionEntry } from '@/types/simulation';
import { importFromFile, importFromImage, exportToJSON, ImportResult } from '@/utils/fileImport';
import { analyzeImageToFormula, ImageAnalysisResult, ExtractedCurve } from '@/utils/imageToFormula';
import { useTheme } from '@/contexts/ThemeContext';
import BatchImageAnalyzer from './BatchImageAnalyzer';

interface Props {
  functions: FunctionEntry[];
  onImport: (functions: Omit<FunctionEntry, 'id'>[], imageOverlay?: { dataUrl: string; width: number; height: number }) => void;
  onClose: () => void;
}

type Tab = 'file' | 'text' | 'image' | 'batch' | 'export';
type ImageMode = 'choose' | 'overlay' | 'formula';

const PRESET_COLORS = ['#00d9ff', '#ff6b35', '#ffd700', '#ff4da6', '#7fff00', '#c084fc', '#ff8c00'];
function pickColor(i: number) { return PRESET_COLORS[i % PRESET_COLORS.length]; }

export default function ImportModal({ functions, onImport, onClose }: Props) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [tab, setTab] = useState<Tab>('file');
  const [dragging, setDragging] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [imagePreview, setImagePreview] = useState<{ dataUrl: string; width: number; height: number } | null>(null);
  const [textInput, setTextInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());

  // 画像モード関連
  const [imageMode, setImageMode] = useState<ImageMode>('choose');
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [analysisResult, setAnalysisResult] = useState<ImageAnalysisResult | null>(null);
  const [selectedCurveIndices, setSelectedCurveIndices] = useState<Set<number>>(new Set());
  const [xRangeMin, setXRangeMin] = useState(-10);
  const [xRangeMax, setXRangeMax] = useState(10);
  const [yRangeMin, setYRangeMin] = useState(-10);
  const [yRangeMax, setYRangeMax] = useState(10);
  const [analyzing, setAnalyzing] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const textPrimary   = isDark ? 'text-white'    : 'text-gray-900';
  const textSecondary = isDark ? 'text-white/60' : 'text-gray-600';
  const textMuted     = isDark ? 'text-white/35' : 'text-gray-400';
  const borderSubtle  = isDark ? 'border-white/10' : 'border-gray-200';

  // ─── ファイル処理 ────────────────────────────────────────────────

  const handleFileProcess = useCallback(async (file: File) => {
    setLoading(true);
    setError(null);
    setResult(null);
    setImagePreview(null);
    setImageMode('choose');
    setPendingImageFile(null);
    setAnalysisResult(null);

    const isImage = file.type.startsWith('image/');
    try {
      if (isImage) {
        // 画像の場合は選択画面へ
        const img = await importFromImage(file);
        setImagePreview(img);
        setPendingImageFile(file);
        setImageMode('choose');
        setTab('image');
      } else {
        const res = await importFromFile(file);
        setResult(res);
        setSelectedIndices(new Set(res.functions.map((_, i) => i)));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : '読み込みに失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileProcess(file);
  }, [handleFileProcess]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileProcess(file);
    e.target.value = '';
  }, [handleFileProcess]);

  const handleImageSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setError(null);
    setAnalysisResult(null);
    setImageMode('choose');
    try {
      const img = await importFromImage(file);
      setImagePreview(img);
      setPendingImageFile(file);
      setImageMode('choose');
    } catch (err) {
      setError(err instanceof Error ? err.message : '画像の読み込みに失敗しました。');
    } finally {
      setLoading(false);
    }
    e.target.value = '';
  }, []);

  // ─── 画像解析（グラフとして読み込む） ────────────────────────────

  const handleAnalyzeImage = useCallback(async () => {
    if (!pendingImageFile) return;
    setAnalyzing(true);
    setError(null);
    setAnalysisResult(null);
    try {
      const result = await analyzeImageToFormula(
        pendingImageFile,
        [xRangeMin, xRangeMax],
        [yRangeMin, yRangeMax],
      );
      setAnalysisResult(result);
      setSelectedCurveIndices(new Set(result.curves.map((_, i) => i)));
      setImageMode('formula');
    } catch (err) {
      setError(err instanceof Error ? err.message : '画像解析に失敗しました。');
    } finally {
      setAnalyzing(false);
    }
  }, [pendingImageFile, xRangeMin, xRangeMax, yRangeMin, yRangeMax]);

  // ─── テキスト解析 ────────────────────────────────────────────────

  const handleTextParse = useCallback(async () => {
    if (!textInput.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const blob = new Blob([textInput], { type: 'text/plain' });
      const file = new File([blob], 'input.txt', { type: 'text/plain' });
      const res = await importFromFile(file);
      setResult(res);
      setSelectedIndices(new Set(res.functions.map((_, i) => i)));
    } catch (e) {
      setError(e instanceof Error ? e.message : '解析に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [textInput]);

  // ─── インポート確定 ──────────────────────────────────────────────

  const toggleSelect = (i: number) => {
    setSelectedIndices((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const toggleCurveSelect = (i: number) => {
    setSelectedCurveIndices((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const handleConfirmImport = () => {
    if (result) {
      const selected = result.functions.filter((_, i) => selectedIndices.has(i));
      onImport(selected);
    }
  };

  const handleImageOverlayImport = () => {
    if (imagePreview) onImport([], imagePreview);
  };

  const handleFormulaImport = () => {
    if (!analysisResult) return;
    const selected = analysisResult.curves
      .filter((_, i) => selectedCurveIndices.has(i))
      .map((curve, i) => ({
        expression: curve.formula,
        color: pickColor(i),
        visible: true,
      }));
    onImport(selected);
  };

  const exportJson = () => {
    const json = exportToJSON(functions);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kuregraph_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const TABS: { key: Tab; icon: string; label: string }[] = [
    { key: 'file',   icon: 'ri-file-upload-line',    label: 'ファイル' },
    { key: 'text',   icon: 'ri-text',                 label: 'テキスト' },
    { key: 'image',  icon: 'ri-image-2-line',         label: '画像' },
    { key: 'batch',  icon: 'ri-stack-line',           label: '一括変換' },
    { key: 'export', icon: 'ri-download-2-line',      label: 'エクスポート' },
  ];

  // ─── R²バッジ ────────────────────────────────────────────────────

  const R2Badge = ({ r2 }: { r2: number }) => {
    const pct = Math.round(r2 * 100);
    const color = r2 >= 0.85 ? 'text-emerald-400' : r2 >= 0.5 ? 'text-yellow-400' : 'text-red-400';
    return (
      <span className={`text-xs font-mono ${color}`}>R²={pct}%</span>
    );
  };

  const typeLabel: Record<ExtractedCurve['type'], string> = {
    polynomial: '多項式',
    sinusoidal: '正弦波',
    exponential: '指数',
    raw: '近似',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div
        className="relative glass rounded-2xl w-[600px] max-h-[85vh] flex flex-col animate-float-in overflow-hidden"
        style={{ boxShadow: '0 24px 60px rgba(0,0,0,0.5)' }}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b ${borderSubtle} flex-shrink-0`}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.18), rgba(0,130,180,0.10))', border: '1px solid rgba(0,180,220,0.25)' }}>
              <i className="ri-import-line text-sm" style={{ color: 'var(--accent-cyan)' }} />
            </div>
            <div>
              <h2 className={`${textPrimary} text-sm font-bold`}>インポート / エクスポート</h2>
              <p className={`${textMuted} text-xs`}>ファイル・テキスト・画像から読み込み</p>
            </div>
          </div>
          <button onClick={onClose} className={`glass-btn ripple-btn w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer ${textMuted} hover:${textPrimary}`}>
            <i className="ri-close-line text-sm" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-4 pt-3 pb-0 flex-shrink-0">
          {TABS.map(({ key, icon, label }) => (
            <button
              key={key}
              onClick={() => { setTab(key); setResult(null); setError(null); }}
              className={`ripple-btn flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                tab === key
                  ? `glass-btn-active ${isDark ? 'text-cyan-300' : 'text-sky-600'}`
                  : `${textMuted} hover:${textSecondary}`
              }`}
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={`${icon} text-xs`} />
              </div>
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar space-y-3">

          {/* ===== FILE TAB ===== */}
          {tab === 'file' && (
            <>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`glass rounded-xl p-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-all duration-200 border-2 border-dashed ${
                  dragging ? 'border-cyan-400/60 bg-cyan-400/5' : `${isDark ? 'border-white/10' : 'border-gray-200'} hover:border-cyan-400/40`
                }`}
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.15), rgba(0,130,180,0.08))', border: '1px solid rgba(0,180,220,0.20)' }}>
                  <i className="ri-upload-cloud-2-line text-2xl" style={{ color: 'var(--accent-cyan)' }} />
                </div>
                <div className="text-center">
                  <p className={`${textPrimary} text-sm font-medium`}>ファイルをドロップ</p>
                  <p className={`${textMuted} text-xs mt-1`}>または クリックして選択</p>
                </div>
                <div className="flex gap-2 flex-wrap justify-center">
                  {['.txt', '.csv', '.json', '.png', '.jpg'].map((ext) => (
                    <span key={ext} className={`glass px-2 py-0.5 rounded-full text-xs ${textMuted}`}>{ext}</span>
                  ))}
                </div>
              </div>
              <input ref={fileInputRef} type="file" accept=".txt,.csv,.json,.tsv,.png,.jpg,.jpeg,.webp" className="hidden" onChange={handleFileSelect} />

              <div className="glass rounded-xl p-3 space-y-2">
                <p className={`${textMuted} text-xs font-medium`}>対応フォーマット</p>
                <div className="space-y-1.5">
                  {[
                    { fmt: 'TXT / CSV', desc: 'y = sin(x) の形式で1行1式' },
                    { fmt: 'JSON',      desc: 'kuregraph エクスポート形式' },
                    { fmt: '画像',      desc: '画像として表示 または グラフ式に自動変換' },
                  ].map(({ fmt, desc }) => (
                    <div key={fmt} className="flex items-start gap-2">
                      <span className={`glass px-1.5 py-0.5 rounded text-xs font-mono ${isDark ? 'text-cyan-300' : 'text-sky-600'} flex-shrink-0`}>{fmt}</span>
                      <span className={`${textMuted} text-xs`}>{desc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ===== TEXT TAB ===== */}
          {tab === 'text' && (
            <>
              <div className="glass rounded-xl overflow-hidden">
                <div className={`px-3 py-2 border-b ${borderSubtle} flex items-center justify-between`}>
                  <span className={`${textMuted} text-xs`}>数式を直接入力</span>
                  <button
                    onClick={() => setTextInput('y = sin(x)\ny = cos(x)\ny = x^2 - 2')}
                    className={`glass-btn ripple-btn text-xs ${textMuted} hover:${textSecondary} px-2 py-0.5 rounded cursor-pointer whitespace-nowrap`}
                  >
                    サンプル挿入
                  </button>
                </div>
                <textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder={'y = sin(x)\ny = cos(x)\ny = x^2 - 2\n\n# コメント行は無視されます'}
                  className={`w-full bg-transparent ${textPrimary} text-sm font-mono p-3 outline-none resize-none h-40 ${isDark ? 'placeholder-white/20' : 'placeholder-gray-300'}`}
                />
              </div>
              <button
                onClick={handleTextParse}
                disabled={!textInput.trim() || loading}
                className="ripple-btn w-full py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap text-white transition-all disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.7), rgba(0,130,180,0.6))', border: '1px solid rgba(0,180,220,0.4)' }}
              >
                {loading ? '解析中...' : '解析する'}
              </button>
            </>
          )}

          {/* ===== IMAGE TAB ===== */}
          {tab === 'image' && (
            <>
              {/* 画像未選択 */}
              {!imagePreview && (
                <div
                  onClick={() => imageInputRef.current?.click()}
                  className={`glass rounded-xl p-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-all duration-200 border-2 border-dashed ${isDark ? 'border-white/10' : 'border-gray-200'} hover:border-cyan-400/40`}
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.15), rgba(0,130,180,0.08))', border: '1px solid rgba(0,180,220,0.20)' }}>
                    <i className="ri-image-2-line text-2xl" style={{ color: 'var(--accent-cyan)' }} />
                  </div>
                  <div className="text-center">
                    <p className={`${textPrimary} text-sm font-medium`}>画像を選択</p>
                    <p className={`${textMuted} text-xs mt-1`}>グラフ画像を読み込んで式に変換、または背景表示</p>
                  </div>
                </div>
              )}

              {/* 画像選択済み → モード選択 */}
              {imagePreview && imageMode === 'choose' && (
                <div className="space-y-3 animate-float-in">
                  {/* プレビュー */}
                  <div className="glass rounded-xl overflow-hidden">
                    <img src={imagePreview.dataUrl} alt="preview" className="w-full object-contain max-h-40" />
                    <div className={`px-3 py-2 border-t ${borderSubtle} flex items-center justify-between`}>
                      <span className={`${textMuted} text-xs`}>{imagePreview.width} × {imagePreview.height}px</span>
                      <button
                        onClick={() => { setImagePreview(null); setPendingImageFile(null); setAnalysisResult(null); }}
                        className={`glass-btn ripple-btn text-xs ${textMuted} hover:${textSecondary} px-2 py-0.5 rounded cursor-pointer whitespace-nowrap`}
                      >
                        変更
                      </button>
                    </div>
                  </div>

                  {/* 読み込み方法の選択 */}
                  <p className={`${textMuted} text-xs font-medium px-1`}>この画像をどのように読み込みますか？</p>
                  <div className="grid grid-cols-2 gap-3">
                    {/* 画像として */}
                    <button
                      onClick={() => setImageMode('overlay')}
                      className="glass ripple-btn rounded-xl p-4 flex flex-col items-center gap-3 cursor-pointer transition-all hover:border-cyan-400/40 border border-transparent text-left"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(255,107,53,0.15), rgba(255,107,53,0.08))', border: '1px solid rgba(255,107,53,0.25)' }}>
                        <i className="ri-image-2-line text-xl" style={{ color: '#ff6b35' }} />
                      </div>
                      <div>
                        <p className={`${textPrimary} text-sm font-semibold`}>画像として表示</p>
                        <p className={`${textMuted} text-xs mt-1 leading-relaxed`}>グラフの背景に半透明でオーバーレイ表示します</p>
                      </div>
                    </button>

                    {/* グラフとして */}
                    <button
                      onClick={() => setImageMode('formula')}
                      className="glass ripple-btn rounded-xl p-4 flex flex-col items-center gap-3 cursor-pointer transition-all hover:border-cyan-400/40 border border-transparent text-left"
                    >
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.15), rgba(0,130,180,0.08))', border: '1px solid rgba(0,180,220,0.25)' }}>
                        <i className="ri-function-line text-xl" style={{ color: 'var(--accent-cyan)' }} />
                      </div>
                      <div>
                        <p className={`${textPrimary} text-sm font-semibold`}>グラフとして読み込む</p>
                        <p className={`${textMuted} text-xs mt-1 leading-relaxed`}>画像を解析して数式に自動変換します</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* 画像オーバーレイモード */}
              {imagePreview && imageMode === 'overlay' && (
                <div className="space-y-3 animate-float-in">
                  <div className="glass rounded-xl overflow-hidden">
                    <img src={imagePreview.dataUrl} alt="preview" className="w-full object-contain max-h-48" />
                  </div>
                  <div className={`glass rounded-xl p-3 flex items-center gap-2`}>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-information-line text-sm" style={{ color: 'var(--accent-cyan)' }} />
                    </div>
                    <p className={`${textMuted} text-xs`}>
                      グラフキャンバスの背景に半透明で表示されます
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setImageMode('choose')}
                      className={`glass-btn ripple-btn flex-1 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap ${textMuted} hover:${textPrimary} transition-all`}
                    >
                      戻る
                    </button>
                    <button
                      onClick={handleImageOverlayImport}
                      className="ripple-btn flex-1 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap text-white transition-all"
                      style={{ background: 'linear-gradient(135deg, rgba(255,107,53,0.7), rgba(255,107,53,0.5))', border: '1px solid rgba(255,107,53,0.4)' }}
                    >
                      背景に追加
                    </button>
                  </div>
                </div>
              )}

              {/* グラフ解析モード */}
              {imagePreview && imageMode === 'formula' && (
                <div className="space-y-3 animate-float-in">
                  {/* 解析前: 設定パネル */}
                  {!analysisResult && (
                    <>
                      <div className="glass rounded-xl overflow-hidden">
                        <img src={imagePreview.dataUrl} alt="preview" className="w-full object-contain max-h-32" />
                      </div>

                      {/* グラフ範囲設定 */}
                      <div className="glass rounded-xl p-3 space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-settings-3-line text-sm" style={{ color: 'var(--accent-cyan)' }} />
                          </div>
                          <span className={`${textPrimary} text-xs font-medium`}>グラフ範囲の設定</span>
                          <span className={`${textMuted} text-xs`}>（画像のグラフ軸に合わせて調整）</span>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <p className={`${textMuted} text-xs`}>X軸範囲</p>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={xRangeMin}
                                onChange={(e) => setXRangeMin(Number(e.target.value))}
                                className={`glass flex-1 min-w-0 px-2 py-1.5 rounded-lg text-xs ${textPrimary} bg-transparent outline-none text-center`}
                                placeholder="-10"
                              />
                              <span className={`${textMuted} text-xs`}>〜</span>
                              <input
                                type="number"
                                value={xRangeMax}
                                onChange={(e) => setXRangeMax(Number(e.target.value))}
                                className={`glass flex-1 min-w-0 px-2 py-1.5 rounded-lg text-xs ${textPrimary} bg-transparent outline-none text-center`}
                                placeholder="10"
                              />
                            </div>
                          </div>
                          <div className="space-y-1.5">
                            <p className={`${textMuted} text-xs`}>Y軸範囲</p>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                value={yRangeMin}
                                onChange={(e) => setYRangeMin(Number(e.target.value))}
                                className={`glass flex-1 min-w-0 px-2 py-1.5 rounded-lg text-xs ${textPrimary} bg-transparent outline-none text-center`}
                                placeholder="-10"
                              />
                              <span className={`${textMuted} text-xs`}>〜</span>
                              <input
                                type="number"
                                value={yRangeMax}
                                onChange={(e) => setYRangeMax(Number(e.target.value))}
                                className={`glass flex-1 min-w-0 px-2 py-1.5 rounded-lg text-xs ${textPrimary} bg-transparent outline-none text-center`}
                                placeholder="10"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => setImageMode('choose')}
                          className={`glass-btn ripple-btn flex-1 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap ${textMuted} hover:${textPrimary} transition-all`}
                        >
                          戻る
                        </button>
                        <button
                          onClick={handleAnalyzeImage}
                          disabled={analyzing}
                          className="ripple-btn flex-1 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap text-white transition-all disabled:opacity-60"
                          style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.7), rgba(0,130,180,0.6))', border: '1px solid rgba(0,180,220,0.4)' }}
                        >
                          {analyzing ? (
                            <span className="flex items-center justify-center gap-2">
                              <div className="w-4 h-4 flex items-center justify-center">
                                <i className="ri-loader-4-line text-sm animate-spin" />
                              </div>
                              解析中...
                            </span>
                          ) : (
                            <span className="flex items-center justify-center gap-2">
                              <div className="w-4 h-4 flex items-center justify-center">
                                <i className="ri-magic-line text-sm" />
                              </div>
                              数式に変換
                            </span>
                          )}
                        </button>
                      </div>
                    </>
                  )}

                  {/* 解析後: 結果表示 */}
                  {analysisResult && (
                    <div className="space-y-3 animate-float-in">
                      {/* プレビュー（検出点オーバーレイ付き） */}
                      <div className="glass rounded-xl overflow-hidden">
                        <img src={analysisResult.previewDataUrl} alt="analysis preview" className="w-full object-contain max-h-36" />
                        <div className={`px-3 py-2 border-t ${borderSubtle} flex items-center gap-2`}>
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className={`text-xs ${analysisResult.curves.length > 0 ? 'ri-check-line text-emerald-400' : 'ri-error-warning-line text-yellow-400'}`} />
                          </div>
                          <span className={`text-xs ${analysisResult.curves.length > 0 ? 'text-emerald-400' : 'text-yellow-400'}`}>
                            {analysisResult.message}
                          </span>
                        </div>
                      </div>

                      {/* 検出された数式 */}
                      {analysisResult.curves.length > 0 ? (
                        <>
                          <div className="flex items-center justify-between">
                            <p className={`${textMuted} text-xs`}>{analysisResult.curves.length}件の数式が検出されました</p>
                            <div className="flex gap-1">
                              <button
                                onClick={() => setSelectedCurveIndices(new Set(analysisResult.curves.map((_, i) => i)))}
                                className={`glass-btn ripple-btn text-xs ${textMuted} hover:${textSecondary} px-2 py-0.5 rounded cursor-pointer whitespace-nowrap`}
                              >
                                全選択
                              </button>
                              <button
                                onClick={() => setSelectedCurveIndices(new Set())}
                                className={`glass-btn ripple-btn text-xs ${textMuted} hover:${textSecondary} px-2 py-0.5 rounded cursor-pointer whitespace-nowrap`}
                              >
                                解除
                              </button>
                            </div>
                          </div>

                          <div className="space-y-2">
                            {analysisResult.curves.map((curve, i) => (
                              <button
                                key={i}
                                onClick={() => toggleCurveSelect(i)}
                                className={`glass ripple-btn w-full flex items-start gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all text-left ${
                                  selectedCurveIndices.has(i) ? 'border-cyan-400/40' : 'opacity-50'
                                }`}
                              >
                                <div className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: pickColor(i) }} />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`glass px-1.5 py-0.5 rounded text-xs ${isDark ? 'text-cyan-300/70' : 'text-sky-600/70'}`}>
                                      {typeLabel[curve.type]}
                                    </span>
                                    <R2Badge r2={curve.r2} />
                                  </div>
                                  <p className={`${textPrimary} text-xs font-mono mt-1 break-all`}>
                                    y = {curve.formula}
                                  </p>
                                </div>
                                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                                  <i className={`text-sm ${selectedCurveIndices.has(i) ? 'ri-checkbox-circle-fill text-cyan-400' : 'ri-checkbox-blank-circle-line text-white/20'}`} />
                                </div>
                              </button>
                            ))}
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => { setAnalysisResult(null); }}
                              className={`glass-btn ripple-btn flex-1 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap ${textMuted} hover:${textPrimary} transition-all`}
                            >
                              再解析
                            </button>
                            <button
                              onClick={handleFormulaImport}
                              disabled={selectedCurveIndices.size === 0}
                              className="ripple-btn flex-1 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap text-white transition-all disabled:opacity-40"
                              style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.7), rgba(0,130,180,0.6))', border: '1px solid rgba(0,180,220,0.4)' }}
                            >
                              {selectedCurveIndices.size}件をグラフに追加
                            </button>
                          </div>
                        </>
                      ) : (
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setAnalysisResult(null); }}
                            className={`glass-btn ripple-btn flex-1 py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap ${textMuted} hover:${textPrimary} transition-all`}
                          >
                            設定を変えて再試行
                          </button>
                          <button
                            onClick={() => setImageMode('overlay')}
                            className="ripple-btn flex-1 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap text-white transition-all"
                            style={{ background: 'linear-gradient(135deg, rgba(255,107,53,0.7), rgba(255,107,53,0.5))', border: '1px solid rgba(255,107,53,0.4)' }}
                          >
                            画像として表示
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
            </>
          )}

          {/* ===== BATCH TAB ===== */}
          {tab === 'batch' && (
            <BatchImageAnalyzer
              onImport={(fns) => onImport(fns)}
            />
          )}

          {/* ===== EXPORT TAB ===== */}
          {tab === 'export' && (
            <div className="space-y-3">
              <div className="glass rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-file-code-line text-sm" style={{ color: 'var(--accent-cyan)' }} />
                  </div>
                  <span className={`${textPrimary} text-sm font-medium`}>JSON形式でエクスポート</span>
                </div>
                <p className={`${textMuted} text-xs leading-relaxed`}>
                  現在の関数 ({functions.length}件) をJSONファイルとして保存します。<br />
                  後でインポートして再利用できます。
                </p>
                <div className="glass rounded-lg p-2.5 font-mono text-xs overflow-auto max-h-32 custom-scrollbar">
                  <pre className={`${isDark ? 'text-cyan-300/70' : 'text-sky-700/70'} whitespace-pre-wrap`}>
                    {JSON.stringify({ version: 1, functions: functions.map(f => ({ expression: f.expression, color: f.color, visible: f.visible })) }, null, 2)}
                  </pre>
                </div>
                <button
                  onClick={exportJson}
                  disabled={functions.length === 0}
                  className="ripple-btn w-full py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap text-white transition-all disabled:opacity-40"
                  style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.7), rgba(0,130,180,0.6))', border: '1px solid rgba(0,180,220,0.4)' }}
                >
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-download-2-line text-sm" />
                    </div>
                    JSONをダウンロード
                  </span>
                </button>
              </div>

              <div className="glass rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-file-text-line text-sm" style={{ color: 'var(--accent-cyan)' }} />
                  </div>
                  <span className={`${textPrimary} text-sm font-medium`}>テキスト形式でエクスポート</span>
                </div>
                <button
                  onClick={() => {
                    const txt = functions.map(f => `y = ${f.expression}`).join('\n');
                    const blob = new Blob([txt], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `kuregraph_${Date.now()}.txt`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  disabled={functions.length === 0}
                  className={`glass-btn ripple-btn w-full py-2.5 rounded-xl text-sm cursor-pointer whitespace-nowrap ${textMuted} hover:${textPrimary} transition-all disabled:opacity-40`}
                >
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-download-line text-sm" />
                    </div>
                    TXTをダウンロード
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center gap-2 py-4">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-loader-4-line text-sm animate-spin" style={{ color: 'var(--accent-cyan)' }} />
              </div>
              <span className={`${textMuted} text-sm`}>処理中...</span>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="glass rounded-xl p-3 border border-red-500/30 flex items-start gap-2">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-error-warning-line text-sm text-red-400" />
              </div>
              <p className="text-red-400 text-xs">{error}</p>
            </div>
          )}

          {/* Results (file/text tab) */}
          {result && (tab === 'file' || tab === 'text') && (
            <div className="space-y-2 animate-float-in">
              <div className="flex items-center justify-between">
                <p className={`${textMuted} text-xs`}>{result.functions.length}件の数式が見つかりました</p>
                <div className="flex gap-1">
                  <button
                    onClick={() => setSelectedIndices(new Set(result.functions.map((_, i) => i)))}
                    className={`glass-btn ripple-btn text-xs ${textMuted} hover:${textSecondary} px-2 py-0.5 rounded cursor-pointer whitespace-nowrap`}
                  >
                    全選択
                  </button>
                  <button
                    onClick={() => setSelectedIndices(new Set())}
                    className={`glass-btn ripple-btn text-xs ${textMuted} hover:${textSecondary} px-2 py-0.5 rounded cursor-pointer whitespace-nowrap`}
                  >
                    解除
                  </button>
                </div>
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto custom-scrollbar">
                {result.functions.map((fn, i) => (
                  <button
                    key={i}
                    onClick={() => toggleSelect(i)}
                    className={`glass ripple-btn w-full flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer transition-all text-left ${
                      selectedIndices.has(i) ? 'border-cyan-400/40' : 'opacity-50'
                    }`}
                  >
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: fn.color }} />
                    <span className={`${textPrimary} text-sm font-mono flex-1 min-w-0 truncate`}>
                      y = {fn.expression}
                    </span>
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className={`text-sm ${selectedIndices.has(i) ? 'ri-checkbox-circle-fill text-cyan-400' : 'ri-checkbox-blank-circle-line text-white/20'}`} />
                    </div>
                  </button>
                ))}
              </div>
              {result.errors.length > 0 && (
                <div className="glass rounded-xl p-2.5 border border-yellow-500/20">
                  {result.errors.map((e, i) => (
                    <p key={i} className="text-yellow-400/80 text-xs">{e}</p>
                  ))}
                </div>
              )}
              <button
                onClick={handleConfirmImport}
                disabled={selectedIndices.size === 0}
                className="ripple-btn w-full py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap text-white transition-all disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.7), rgba(0,130,180,0.6))', border: '1px solid rgba(0,180,220,0.4)' }}
              >
                {selectedIndices.size}件をグラフに追加
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
